import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Mic, Loader2, ArrowRight, ArrowLeft, Sparkles, CheckCircle2,
  AlertTriangle, Lightbulb, Target, RotateCcw, PenTool, Compass, Terminal, Code2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AvatarQuestionPlayer from "@/components/interview/AvatarQuestionPlayer";
import ProctoringConsentScreen from "@/components/interview/ProctoringConsentScreen";
import ProctoringStatusBar from "@/components/interview/ProctoringStatusBar";
import WhiteboardCanvas from "@/components/interview/WhiteboardCanvas";
import CodePlaygroundPanel from "@/components/interview/CodePlaygroundPanel";
import { useProctoring } from "@/hooks/useProctoring";

const SUB_SCORES = [
  { key: "technical_score", label: "Technical Depth", color: "#3B82F6" },
  { key: "communication_score", label: "Communication", color: "#6366F1" },
  { key: "problem_solving_score", label: "Problem Solving", color: "#A78BFA" },
  { key: "confidence_score", label: "Confidence", color: "#FBBF24" },
];

function ScoreGauge({ score }) {
  const color = score >= 80 ? "#10B981" : score >= 50 ? "#FBBF24" : "#F87171";
  const circumference = 2 * Math.PI * 46;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-28 h-28 shrink-0" data-testid="interview-result-score-gauge">
      <svg className="w-28 h-28 -rotate-90">
        <circle cx="56" cy="56" r="46" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
        <motion.circle
          cx="56" cy="56" r="46" stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={circumference} strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-2xl font-bold text-white" data-testid="interview-result-overall-score">{score}</span>
        <span className="text-[10px] text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

export default function InterviewSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [diagramDrafts, setDiagramDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [result, setResult] = useState(null);
  const whiteboardRef = useRef(null);
  const proctoring = useProctoring({ sessionId, sessionType: "classic", onTerminate: () => navigate("/interview") });

  // Built-in Coding Playground state — code/language per question index, plus the last "Run" result.
  const [codeLanguages, setCodeLanguages] = useState([]);
  const [codeDrafts, setCodeDrafts] = useState({});
  const [langDrafts, setLangDrafts] = useState({});
  const [runResults, setRunResults] = useState({});
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/interview/coding-languages").then((r) => setCodeLanguages(r.data)).catch(() => setCodeLanguages([]));
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get(`/interview/${sessionId}`).then(({ data }) => {
      if (!mounted) return;
      setSession(data);
      if (data.status === "completed") {
        setResult(data.result);
      } else {
        const firstUnanswered = data.questions.findIndex((q) => !q.answer);
        setCurrentIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
        if (data.type === "coding") {
          const codeSeed = {}, langSeed = {}, runSeed = {};
          data.questions.forEach((q, i) => {
            langSeed[i] = q.language || "python";
            codeSeed[i] = q.code || (q.starter_code || {})[langSeed[i]] || "";
            if (q.test_results) runSeed[i] = q.test_results;
          });
          setCodeDrafts(codeSeed);
          setLangDrafts(langSeed);
          setRunResults(runSeed);
        } else {
          const seeded = {};
          data.questions.forEach((q, i) => { if (q.answer) seeded[i] = q.answer; });
          setDrafts(seeded);
        }
      }
      setLoading(false);
    }).catch(() => {
      toast.error("Interview session not found");
      navigate("/interview");
    });
    return () => { mounted = false; };
  }, [sessionId, navigate]);

  const isCoding = session?.type === "coding";
  const totalQuestions = session?.questions?.length || 0;
  const currentQuestion = session?.questions?.[currentIndex];
  const isLast = currentIndex === totalQuestions - 1;
  const allAnswered = isCoding
    ? session?.questions?.every((q, i) => codeDrafts[i]?.trim())
    : session?.questions?.every((q, i) => drafts[i]?.trim());

  const isSavingRef = useRef(false);

  const handleLanguageChange = (lang) => {
    setLangDrafts((d) => ({ ...d, [currentIndex]: lang }));
    const q = session.questions[currentIndex];
    // Only reset to the new language's starter stub if the candidate hasn't customized this
    // question's code yet under this language (avoid nuking a language they already wrote in before).
    if (!q.code) {
      setCodeDrafts((d) => ({ ...d, [currentIndex]: (q.starter_code || {})[lang] || "" }));
    }
  };

  const handleRunCode = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/interview/${sessionId}/code/run`, {
        question_index: currentIndex, code: codeDrafts[currentIndex] || "", language: langDrafts[currentIndex] || "python",
      });
      setRunResults((r) => ({ ...r, [currentIndex]: data.test_results }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const saveCurrentAnswer = async () => {
    if (isSavingRef.current) return false;
    isSavingRef.current = true;
    setSaving(true);
    try {
      if (isCoding) {
        const code = (codeDrafts[currentIndex] || "").trim();
        if (!code) {
          toast.error("Please write some code before continuing.");
          return false;
        }
        const { data } = await api.post(`/interview/${sessionId}/answer`, {
          question_index: currentIndex, code, language: langDrafts[currentIndex] || "python",
        });
        setRunResults((r) => ({ ...r, [currentIndex]: data.test_results }));
        return true;
      }
      const answer = (drafts[currentIndex] || "").trim();
      if (!answer) {
        toast.error("Please write an answer before continuing.");
        return false;
      }
      const payload = { question_index: currentIndex, answer };
      if (session?.type === "architecture" && whiteboardRef.current && !whiteboardRef.current.isEmpty()) {
        payload.diagram_image = whiteboardRef.current.exportImage();
      }
      const { data } = await api.post(`/interview/${sessionId}/answer`, payload);
      if (data.diagram_feedback) {
        setSession((s) => ({
          ...s,
          questions: s.questions.map((q, i) => (i === currentIndex ? { ...q, diagram_feedback: data.diagram_feedback } : q)),
        }));
        const fb = data.diagram_feedback;
        if (fb.score !== null && fb.score !== undefined) {
          toast.success(`Diagram scored ${fb.score}/100 — ${fb.feedback}`, { id: `diagram-feedback-${sessionId}-${currentIndex}`, duration: 6000 });
        }
      }
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  };

  const handleNext = async () => {
    const ok = await saveCurrentAnswer();
    if (ok) setCurrentIndex((i) => Math.min(i + 1, totalQuestions - 1));
  };

  const handleBack = () => setCurrentIndex((i) => Math.max(i - 1, 0));

  const handleComplete = async () => {
    const ok = await saveCurrentAnswer();
    if (!ok) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/interview/${sessionId}/complete`);
      setResult(data);
      proctoring.finalizeSession();
      toast.success("Interview evaluated!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 flex items-center justify-center" data-testid="interview-session-loading">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (completing) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 flex flex-col items-center justify-center text-center" data-testid="interview-evaluating-state">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mb-4" />
        <p className="text-white font-heading text-lg font-semibold">Analyzing your interview…</p>
        <p className="text-sm text-slate-400 mt-1.5">Our AI hiring manager is reviewing your answers for technical depth, communication, and confidence.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="interview-results-page">
        <div className="flex items-center gap-3 mb-2">
          <Mic className="w-6 h-6 text-indigo-400" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400 capitalize">{session?.type} Interview · Result</span>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-white">Interview Report</h1>

        <div className="glass-panel rounded-2xl p-6 mt-6 flex items-center gap-6 border border-indigo-400/20" data-testid="interview-result-summary-card">
          <ScoreGauge score={result.overall_score} />
          <p className="text-sm text-slate-300 leading-relaxed flex-1" data-testid="interview-result-summary-text">{result.interview_summary}</p>
        </div>

        {result.level_assessment && (
          <div className="glass-panel rounded-2xl p-5 mt-4 border border-indigo-400/20" data-testid="career-intelligence-card">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-2"><Compass className="w-4 h-4" /> AI Career Intelligence</h4>
            <p className="text-sm text-slate-200 leading-relaxed" data-testid="career-level-assessment">{result.level_assessment}</p>
            {result.estimated_prep_days != null && (
              <p className="text-xs text-slate-400 mt-2" data-testid="career-estimated-prep-days">Estimated preparation time to reach the next level: <span className="text-white font-semibold">{result.estimated_prep_days} days</span></p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {SUB_SCORES.map((s) => (
            <div key={s.key} className="glass-panel rounded-xl p-3.5" data-testid={`interview-subscore-${s.key}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{s.label}</p>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-1.5">
                <motion.div
                  className="h-full rounded-full" style={{ backgroundColor: s.color }}
                  initial={{ width: 0 }} animate={{ width: `${result[s.key] ?? 0}%` }} transition={{ duration: 0.8 }}
                />
              </div>
              <span className="text-sm font-bold text-white">{result[s.key] ?? 0}</span>
            </div>
          ))}
        </div>

        {result.diagram_score !== undefined && (
          <div className="glass-panel rounded-2xl p-4 mt-4 border border-amber-400/20" data-testid="interview-diagram-score-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5"><PenTool className="w-3.5 h-3.5" /> System Design / Whiteboard Score</p>
              <span className="text-lg font-heading font-bold text-white" data-testid="interview-diagram-score-value">{result.diagram_score}/100</span>
            </div>
            <div className="space-y-2.5">
              {(result.diagrams || []).map((d, i) => (
                <div key={i} className="bg-white/[0.03] rounded-lg p-3" data-testid={`interview-diagram-feedback-${i}`}>
                  <p className="text-xs text-slate-400 mb-1">Q{d.question_index + 1}: {d.question}</p>
                  <p className="text-xs text-slate-300">{d.feedback}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.communication_coach && (
          <div className="glass-panel rounded-2xl p-4 mt-4 border border-violet-400/20" data-testid="interview-communication-coach-card">
            <p className="text-[11px] font-bold uppercase tracking-wider text-violet-400 flex items-center gap-1.5 mb-3">
              <Compass className="w-3.5 h-3.5" /> AI Communication Coach
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                <p className="text-2xl font-heading font-bold text-white" data-testid="interview-eye-contact-score">{result.communication_coach.eye_contact_score}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Eye Contact</p>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                <p className="text-2xl font-heading font-bold text-white" data-testid="interview-confidence-score">{result.communication_coach.confidence_score}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Confidence & Body Language</p>
              </div>
            </div>
            {result.communication_coach.notes?.length > 0 && (
              <ul className="space-y-1.5">
                {result.communication_coach.notes.map((note, i) => (
                  <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5"><span className="text-violet-400 mt-0.5">•</span> {note}</li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-slate-500 mt-2.5">Based on {result.communication_coach.sample_count} webcam snapshots analyzed during the interview.</p>
          </div>
        )}

        {result.coding_results && (
          <div className="glass-panel rounded-2xl p-4 mt-4 border border-purple-400/20" data-testid="interview-coding-results-card">
            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-400 flex items-center gap-1.5 mb-3"><Terminal className="w-3.5 h-3.5" /> Coding Playground — Real Test Results</p>
            <div className="space-y-2.5">
              {result.coding_results.map((c) => (
                <div key={c.question_index} className="bg-white/[0.03] rounded-lg p-3" data-testid={`interview-coding-result-${c.question_index}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs text-slate-300 font-semibold">Q{c.question_index + 1}: {c.question} <span className="text-slate-500 font-normal">({c.language})</span></p>
                    <span className={`text-xs font-bold shrink-0 ${c.passed_count === c.total_count ? "text-emerald-400" : "text-amber-400"}`} data-testid={`interview-coding-pass-rate-${c.question_index}`}>
                      {c.passed_count}/{c.total_count} tests passed
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1.5">
                    <div className={`h-full rounded-full ${c.passed_count === c.total_count ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${c.total_count ? (c.passed_count / c.total_count) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="glass-panel rounded-2xl p-4" data-testid="interview-result-areas-to-improve">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> Areas to Improve</p>
            <ul className="space-y-1.5">
              {(result.areas_to_improve || []).map((a) => <li key={a} className="text-xs text-slate-400">• {a}</li>)}
            </ul>
          </div>
          <div className="glass-panel rounded-2xl p-4" data-testid="interview-result-recommended-challenges">
            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-400 flex items-center gap-1.5 mb-2"><Lightbulb className="w-3.5 h-3.5" /> Recommended Practice</p>
            <ul className="space-y-1.5">
              {(result.recommended_challenges || []).map((a) => <li key={a} className="text-xs text-slate-400">• {a}</li>)}
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <button onClick={() => navigate("/interview")} data-testid="interview-back-to-center-btn"
            className="flex items-center gap-2 text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2.5 rounded-lg">
            <RotateCcw className="w-4 h-4" /> Take Another Interview
          </button>
          <Link to="/dashboard" data-testid="interview-back-to-dashboard-link"
            className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white px-4 py-2.5 rounded-lg border border-white/10">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!proctoring.consentGiven) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <ProctoringConsentScreen starting={proctoring.starting} error={proctoring.error} onConsent={proctoring.requestConsent} />
      </div>
    );
  }

  return (
    <div className={`${isCoding ? "max-w-5xl" : "max-w-3xl"} mx-auto px-4 sm:px-6 py-8`} data-testid="interview-session-page">
      <ProctoringStatusBar webcamVideoRef={proctoring.webcamVideoRef} violationCount={proctoring.violationCount} violationLimit={proctoring.violationLimit} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1.5">
            {isCoding ? <Terminal className="w-5 h-5 text-purple-400" /> : <Mic className="w-5 h-5 text-indigo-400" />}
            <span className={`text-xs font-bold uppercase tracking-[0.2em] ${isCoding ? "text-purple-400" : "text-indigo-400"} capitalize`}>{session?.type} Interview</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white">Question {currentIndex + 1} of {totalQuestions}</h1>
        </div>
        {currentQuestion?.focus_area && (
          <span className="text-xs font-semibold bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-slate-300" data-testid="interview-focus-area-badge">
            {currentQuestion.focus_area}
          </span>
        )}
      </div>

      <div className="flex gap-1.5 mb-6" data-testid="interview-progress-bar">
        {session?.questions?.map((q, i) => {
          const done = isCoding ? codeDrafts[i]?.trim() : drafts[i]?.trim();
          return <div key={q.question || i} className={`h-1.5 flex-1 rounded-full ${i < currentIndex || done ? "bg-indigo-400" : i === currentIndex ? "bg-indigo-400/40" : "bg-white/5"}`} />;
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
          className="glass-panel rounded-2xl p-6"
        >
          {isCoding ? (
            <>
              <p className="text-base text-white leading-relaxed font-medium flex items-center gap-2" data-testid="interview-question-text">
                <Code2 className="w-4 h-4 text-purple-400" /> {currentQuestion?.question}
              </p>
              <CodePlaygroundPanel
                problem={currentQuestion}
                code={codeDrafts[currentIndex] || ""}
                language={langDrafts[currentIndex] || "python"}
                onCodeChange={(v) => setCodeDrafts((d) => ({ ...d, [currentIndex]: v }))}
                onLanguageChange={handleLanguageChange}
                onRun={handleRunCode}
                running={running}
                result={runResults[currentIndex]}
                languages={codeLanguages}
              />
            </>
          ) : (
            <>
              <AvatarQuestionPlayer questionText={currentQuestion?.question} accent="indigo" />
              <p className="text-base text-white leading-relaxed font-medium" data-testid="interview-question-text">{currentQuestion?.question}</p>

              {session?.type === "architecture" && (
                <div className="mt-4" data-testid="interview-whiteboard-section">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1.5 mb-2">
                    <PenTool className="w-3.5 h-3.5" /> Whiteboard — sketch your architecture (optional)
                  </p>
                  <WhiteboardCanvas
                    ref={whiteboardRef}
                    shapes={diagramDrafts[currentIndex] || []}
                    onChange={(next) => setDiagramDrafts((d) => ({ ...d, [currentIndex]: next }))}
                  />
                  {currentQuestion?.diagram_feedback && (
                    <div className="mt-3 bg-white/[0.03] border border-indigo-400/20 rounded-xl p-3.5" data-testid="interview-diagram-feedback-card">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold text-indigo-300">AI Diagram Feedback</span>
                        {currentQuestion.diagram_feedback.score !== null && currentQuestion.diagram_feedback.score !== undefined && (
                          <span className="text-sm font-bold text-white">{currentQuestion.diagram_feedback.score}/100</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-300">{currentQuestion.diagram_feedback.feedback}</p>
                    </div>
                  )}
                </div>
              )}

              <textarea
                value={drafts[currentIndex] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [currentIndex]: e.target.value }))}
                placeholder="Type your answer here as you would in a real interview..."
                rows={8}
                data-testid="interview-answer-textarea"
                className="w-full mt-4 bg-white/[0.03] border border-white/10 rounded-xl p-4 text-sm text-slate-200 placeholder:text-slate-400 focus:outline-none focus:border-indigo-400/40 resize-none"
              />
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={handleBack} disabled={currentIndex === 0} data-testid="interview-prev-btn"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>

        {isLast ? (
          <button
            onClick={handleComplete} disabled={saving || !allAnswered} data-testid="interview-finish-btn"
            className="flex items-center gap-2 text-sm font-semibold bg-emerald-500 hover:bg-emerald-500/90 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Finish & Get AI Score
          </button>
        ) : (
          <button
            onClick={handleNext} disabled={saving} data-testid="interview-next-btn"
            className="flex items-center gap-2 text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Save & Next
          </button>
        )}
      </div>

      {isLast && !allAnswered && (
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5" data-testid="interview-answer-all-hint">
          <Target className="w-3.5 h-3.5" /> Answer this question to unlock your AI evaluation.
        </p>
      )}
    </div>
  );
}
